import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Link, useHistory } from 'react-router-dom';
import { EnvelopeIcon, UserIcon, KeyIcon } from '@heroicons/react/24/solid';
import { api } from '../api';
import signUpPicture from '../images/sara_left.png';
import Background from '../images/bg-2.png';
import LogoA from '../images/logo_txt_blanc.png';
import { REMOTE_PRODUCT_SELECTED_KEY } from '../constants';
import ToastAction from '../components/ToastAction';

const SignupForm = () => {
  const [user, setUser] = useState({});
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const handleLogin = useCallback(
    (e) => {
      e.preventDefault();

      if (user.password.trim() !== user.confPassword.trim())
        return toast.custom((t) => <ToastAction saraExpression="thinking" toastRef={t} label="Les mots de passe ne sont pas identiques." description="Assurez-vous que vos mots de passe sont parfaitement identiques." />)

      let userObj = {
        username: user.email,
        password: user.password,
        attributes: {
          family_name: user.family_name,
          name: user.name,
          phone_number: ''
        }
      };
      setLoading(true);
      api
        .auth()
        .signUp(userObj)
        .then(() => {
          setLoading(false);
          const params = history.location.search
          if (params && params.split('=').length > 0) {
            const product_id = params.split('=')[1]
            localStorage.setItem(REMOTE_PRODUCT_SELECTED_KEY, product_id)
          }
          history.push('/check-mailbox');
        }).catch((err) => {
          setLoading(false)
          if ('UsernameExistsException' == err.code) toast.custom((t) => <ToastAction saraExpression="thinking" toastRef={t} label="Cet email existe déjà !" description="Voulez-vous vous connecter ?" okAction={() => history.push('/login')} />)
          else if ('InvalidPasswordException' == err.code) toast.custom((t) => <ToastAction saraExpression="thinking" toastRef={t} label="Votre mot de passe n'est pas valide." description="Pour assurer la sécurité de votre compte, veuillez respecter les critères de sécuruté de mot de passe SVP !" />)
          else toast.custom((t) => <ToastAction saraExpression="sorry" toastRef={t} label="Oups !!" description="Nous avons rencontré un problème. Vérifier votre connexion internet ou alors signalez-nous si l'erreur persiste." />)
        });
    },
    [user]
  );

  const handleChange = useCallback((e) => {
    const newUser = {};
    newUser[e.target.id] = e.target.value;
    setUser((s) => ({ ...s, ...newUser }));
  }, []);

  return (
    <div className="bg-blue-700 min-h-screen flex items-center justify-end">
      <img src={Background} className="fixed z-0 w-full h-full object-cover" alt="Background" />
      <div className="max-w-sm hidden lg:block z-50 mx-auto text-center space-y-4">
        <h1 className="text-2xl text-white tracking-wider">Bienvenue sur</h1>
        <img src={LogoA} className="w-64 mx-auto" alt="Background" />
        <p className="text-sm text-white tracking-wider">Connectez-vous à sur notre plateforme Azatys</p>
        <p className="text-xs text-white tracking-wider">
          Rentrez dans notre univers, vous aurez bientôt toutes les cartes en main pour développer votre activité
          digitale
        </p>
      </div>
      <div className="relative md:ml-24 xl:ml-0 z-10 bg-general-blue shadow text-white py-8 px-16 rounded-3xl mx-6 lg:max-w-xl xl:max-w-2xl w-full md:mr-20">
        <img src={signUpPicture} alt="Sara" className="hidden lg:block absolute h-80 transform translate-x-24 -left-64" />
        <div className="space-y-8">
          <div className="md:col-span-1 mx-auto text-center">
            <h3 className="text-3xl font-medium leading-6 tracking-widest">Inscription</h3>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-2">
            <form onSubmit={handleLogin} method="POST">
              <div className="flex flex-col justify-center gap-3">
                <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                  <div className="bg-white w-full border-gray-300 rounded-full overflow-clip flex items-center text-gray-500 mt-1 px-4 py-3 border shadow-sm">
                    <input
                      onChange={handleChange}
                      type="text"
                      name="name"
                      id="name"
                      placeholder="Prenom"
                      autoComplete="given_name"
                      className="sm:text-sm flex-1"
                      required
                    />
                    <div className="flex-shrink">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                  <div className="bg-white w-full border-gray-300 rounded-full overflow-clip flex items-center text-gray-500 mt-1 px-4 py-3 border shadow-sm">
                    <input
                      onChange={handleChange}
                      type="text"
                      name="family_name"
                      id="family_name"
                      placeholder="Nom"
                      autoComplete="family_name"
                      className="sm:text-sm flex-1"
                      required
                    />
                    <div className="flex-shrink">
                      <UserIcon className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                </div>

                <div className="bg-white w-full border-gray-300 rounded-full overflow-clip flex items-center text-gray-500 mt-1 px-4 py-3 border shadow-sm">
                  <input
                    onChange={handleChange}
                    type="text"
                    name="email"
                    id="email"
                    placeholder="Adresse-email"
                    autoComplete="email"
                    className="sm:text-sm flex-1"
                    required
                  />
                  <div className="flex-shrink">
                    <EnvelopeIcon className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div className="bg-white w-full border-gray-300 rounded-full overflow-clip flex items-center text-gray-500 mt-1 px-4 py-3 border shadow-sm">
                  <input
                    onChange={handleChange}
                    type="password"
                    name="password"
                    id="password"
                    minLength={4}
                    placeholder="Mot de passe"
                    autoComplete="password"
                    className="sm:text-sm flex-1"
                    required
                  />
                  <div className="flex-shrink">
                    <KeyIcon className="w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div className="bg-white w-full border-gray-300 rounded-full overflow-clip flex items-center text-gray-500 mt-1 px-4 py-3 border shadow-sm">
                  <input
                    onChange={handleChange}
                    type="password"
                    name="confPassword"
                    minLength={4}
                    id="confPassword"
                    placeholder="Confirmer votre mot de passe"
                    className="sm:text-sm flex-1"
                    required
                  />
                  <div className="flex-shrink">
                    <KeyIcon className="w-4 h-4 text-gray-500" />
                  </div>
                </div>

                <div className="text-xs p-2">
                  <p>Pour que votre mot de passe soit valide, il doit au moins:</p>
                  <li>
                    Contenir <strong>une minuscule</strong>, <strong>une majuscule</strong>, un chiffre et{' '}
                    <strong>un caractère spécial.</strong>
                  </li>
                  <li>Avoir au minimum 8 caractères.</li>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="mt-4 w-full uppercase flex justify-center py-3 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Inscription...' : "S'inscrire"}
                </button>

                <div className="text-sm space-x-2 items-center justify-center flex">
                  <p>Vous avez deja un compte?</p>
                  <Link to="/login" className="font-medium text-xs underline text-white hover:text-teal-500 transition-colors">
                    Se connecter
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};
export default SignupForm;
